import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const Roa = () => {
  // State variables for ROA Calculation
  const [netIncome, setNetIncome] = useState(0); // Net Income
  const [totalAssets, setTotalAssets] = useState(0); // Total Assets
  const [roaResult, setRoaResult] = useState(0); // Calculated ROA result
  const [currency, setCurrency] = useState("USD"); // Selected currency

  // Currency symbols (for display)
  const currencySymbols = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    INR: "₹",
    JPY: "¥",
    AUD: "A$",
    CAD: "C$",
    CHF: "Fr",
    CNY: "¥",
    ZAR: "R"
  };

  // Event handlers to update state values
  const handleNetIncomeChange = (e) => setNetIncome(parseFloat(e.target.value) || 0);
  const handleTotalAssetsChange = (e) => setTotalAssets(parseFloat(e.target.value) || 0);

  // Event handler to change currency
  const handleCurrencyChange = (e) => setCurrency(e.target.value);

  // useEffect hook to calculate ROA when any input changes
  useEffect(() => {
    // ROA formula: ROA = (Net Income / Total Assets) * 100
    if (totalAssets > 0) {
      const roaCalculation = (netIncome / totalAssets) * 100;
      setRoaResult(roaCalculation);
    } else {
      setRoaResult(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [netIncome, totalAssets]);

  return (
    <Layout title="Return on Assets (ROA) Calculator" path="roa-calculator">
      <section className="section-padding" id="roa-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* ROA Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Return on Assets (ROA) Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Net Income Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="netIncome" className="form-label">
                        Net Income
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Net Income:</strong> Enter the total earnings of the company after deducting all expenses, taxes, and costs."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="netIncome"
                        placeholder="Enter net income"
                        value={netIncome || ""}
                        onChange={handleNetIncomeChange}
                      />
                    </div>

                    {/* Total Assets Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalAssets" className="form-label">
                        Total Assets
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Assets:</strong> Enter the total value of all assets owned by the company."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalAssets"
                        placeholder="Enter total assets"
                        value={totalAssets || ""}
                        onChange={handleTotalAssetsChange}
                      />
                    </div>

                    {/* Currency Selection */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="currency" className="form-label">
                        Select Currency
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Select Currency:</strong> Choose the currency to display the result in."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <select
                        className="form-select"
                        id="currency"
                        value={currency}
                        onChange={handleCurrencyChange}
                      >
                        {Object.keys(currencySymbols).map((currencyCode) => (
                          <option key={currencyCode} value={currencyCode}>
                            {currencyCode} ({currencySymbols[currencyCode]})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        ROA:{" "}
                        <span className="fw-bold ms-3">
                          {roaResult === 0 ? "0.00" : roaResult.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %
                        </span>
                        {/* <span className="ms-2">{currencySymbols[currency]}</span> */}
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Return on Assets (ROA) Calculator
                </h3>

                {/* What is a ROA Calculator? */}
                <h4 className="mt-3 mb-3">What is a Return on Assets (ROA) Calculator?</h4>
                <p>
                  The **Return on Assets (ROA) Calculator** is a financial tool that helps assess how efficiently a company uses its assets to generate profits. By calculating the ROA, businesses can understand their asset efficiency and make more informed decisions.
                </p>

                {/* Why Use a ROA Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Return on Assets (ROA) Calculator?</h4>
                <p>
                  The ROA ratio is essential for measuring the profitability of a company relative to its assets. A higher ROA indicates that the company is efficiently using its assets to generate profit.
                </p>

                {/* Benefits of Using a ROA Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Return on Assets (ROA) Calculator</h4>
                <ul>
                  <li><strong>Assesses asset efficiency and profitability:</strong> ROA helps businesses understand how well they are utilizing their assets to generate earnings.</li>
                  <li><strong>Supports investment decision-making:</strong> Investors use ROA to determine how effectively companies are using their resources, aiding in investment decisions.</li>
                  <li><strong>Useful for tracking company performance:</strong> ROA can be used over time to monitor changes in a company’s efficiency and profitability.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Return on Assets (ROA) Calculator?</h4>
                <p>
                  The Return on Assets (ROA) Calculator is ideal for:
                </p>
                <ul>
                  <li><strong>Corporations:</strong> To assess how efficiently they are using their assets to generate profit.</li>
                  <li><strong>Investors:</strong> For evaluating companies and making informed investment decisions.</li>
                  <li><strong>Financial Analysts:</strong> To track and compare the performance of companies using ROA as a key financial metric.</li>
                </ul>

                {/* Two Additional Questions */}
                <h4 className="mt-4 mb-3">How Can ROA Help Improve Financial Decision-Making?</h4>
                <p>
                  ROA helps businesses identify whether they are utilizing their assets effectively. If ROA is low, companies can look for ways to improve asset efficiency or rethink their strategies.
                </p>

                <h4 className="mt-4 mb-3">Is ROA Useful for Small and Large Companies Alike?</h4>
                <p>
                  Yes, ROA is a universal metric. Both small and large companies can use ROA to gauge how well they are using their assets to generate profits, though it might be analyzed differently depending on the scale of operations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Roa;
